import React from "react";
import { graphql, readInlineData, useFragment } from "react-relay";

import { faCalendar, faRepeat } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { meetingDateFormat } from "~/lib/utils";
import { MeetingInfoModalWrapper } from "~/nav/panels/Meetings/MeetingInfoModal";
import { parseRecurrenceRule } from "~/stream/utils";
import { ButtonLink, HelpCenterLink } from "~/ui/legacyFellowIndex";
import { useModals } from "~/ui/modals";

import { ReactComponent as EmptyStateIllustration } from "./empty-state-illustration.svg";

import type { CalendarEvents_meetingInfoModalEvent$key } from "~/__generated__/CalendarEvents_meetingInfoModalEvent.graphql";
import type { CalendarEvents_NoteStream$key } from "~/__generated__/CalendarEvents_NoteStream.graphql";
import type { CalendarEventsRecurringEventCard_Event$key } from "~/__generated__/CalendarEventsRecurringEventCard_Event.graphql";
import type { CalendarEventsSingleEventCard_Event$key } from "~/__generated__/CalendarEventsSingleEventCard_Event.graphql";

import styles from "./CalendarEvents.module.css";

export function CalendarEvents({ streamKey }: { streamKey: CalendarEvents_NoteStream$key }) {
	const stream = useFragment(
		graphql`
			fragment CalendarEvents_NoteStream on NoteStream {
				claimedEvents {
					type
					lastRecurrenceEnd
					end
					...CalendarEventsSingleEventCard_Event
					...CalendarEventsRecurringEventCard_Event
				}
			}
		`,
		streamKey,
	);

	if (!stream.claimedEvents.length) {
		return <EmptyState />;
	}

	type Event = (typeof stream)["claimedEvents"][number];
	const futureEvents: Event[] = [],
		finishedEvents: Event[] = [];

	for (const event of stream.claimedEvents) {
		let isFuture;
		if (event.type === "SINGLE" && event.end) {
			isFuture = new Date(event.end).valueOf() > Date.now();
		} else if (event.type === "RECURRING") {
			isFuture = !event.lastRecurrenceEnd || new Date(event.lastRecurrenceEnd).valueOf() > Date.now();
		} else {
			continue;
		}
		(isFuture ? futureEvents : finishedEvents).push(event);
	}

	return (
		<main className={styles.main}>
			<aside className={styles.explanation}>
				<p>
					Notes are automatically created for associated calendar events. Learn more about{" "}
					<HelpCenterLink articleId="4892111">notes and calendar events</HelpCenterLink>.{" "}
				</p>
			</aside>

			<div>
				{futureEvents.map(ev =>
					ev.type === "RECURRING" ? (
						<RecurringEventCard eventKey={ev} />
					) : ev.type === "SINGLE" ? (
						<SingleEventCard eventKey={ev} />
					) : null,
				)}
				{!!futureEvents.length && !!finishedEvents.length && <hr className={styles.horizontalRule} />}
				{finishedEvents.map(ev =>
					ev.type === "RECURRING" ? (
						<RecurringEventCard eventKey={ev} />
					) : ev.type === "SINGLE" ? (
						<SingleEventCard eventKey={ev} />
					) : null,
				)}
			</div>
		</main>
	);
}

function Card({ children }: React.PropsWithChildren<{}>) {
	return <article className={styles.eventCard}>{children}</article>;
}

function Metadata({ icon, children }: React.PropsWithChildren<{ icon: IconDefinition }>) {
	return (
		<p className={styles.metadata}>
			<FontAwesomeIcon icon={icon} fixedWidth />
			<span className={styles.text}>{children}</span>
		</p>
	);
}

function ViewEventInfoButton({ event }: { event: CalendarEvents_meetingInfoModalEvent$key }) {
	const modalCtx = useModals();

	const openMeetingInfoModal = React.useCallback(() => {
		// eslint-disable-next-line fellow/no-inline-disable
		// eslint-disable-next-line react/jsx-no-bind
		modalCtx.open(({ close }) => (
			<MeetingInfoModalWrapper onClose={close} eventGuid={readMeetingInfoModalEvent(event).guid} />
		));
	}, [event, modalCtx]);

	return (
		<ButtonLink className={styles.viewInfoButton} onClick={openMeetingInfoModal}>
			View event info
		</ButtonLink>
	);
}

function RecurringEventCard({ eventKey }: { eventKey: CalendarEventsRecurringEventCard_Event$key }) {
	const event = useFragment(
		graphql`
			fragment CalendarEventsRecurringEventCard_Event on Event {
				id
				start
				end
				title
				recurrenceRule
				isAllDay

				...CalendarEvents_meetingInfoModalEvent
			}
		`,
		eventKey,
	);

	const rrule = React.useMemo(() => {
		if (!event.recurrenceRule) return null;
		return parseRecurrenceRule(event.recurrenceRule);
	}, [event]);

	const rruleString = React.useMemo(() => rrule?.toText(), [rrule]);

	const nextEventString = React.useMemo(() => {
		if (!rrule) return null;
		const now = new Date();
		const nextEventDate = rrule.after(now);
		if (!nextEventDate) return null;
		if (!event.start || !event.end) return null;
		const startDate = new Date(event.start);
		// Since the recurrence rule doesn't have the time in it, we need to set
		// that manually on the resulting date, otherwise it will just use the
		// current time.
		nextEventDate.setHours(
			startDate.getHours(),
			startDate.getMinutes(),
			startDate.getSeconds(),
			startDate.getMilliseconds(),
		);
		const durationMilliseconds = new Date(event.end).valueOf() - startDate.valueOf();
		return formatEventDateTime(nextEventDate, durationMilliseconds, event.isAllDay);
	}, [rrule, event]);

	return (
		<Card>
			<h1>{event.title}</h1>
			<Metadata icon={faRepeat}>Repeats {rruleString}</Metadata>
			{nextEventString && <Metadata icon={faCalendar}>Next: {nextEventString}</Metadata>}
			<ViewEventInfoButton event={event} />
		</Card>
	);
}

function SingleEventCard({ eventKey }: { eventKey: CalendarEventsSingleEventCard_Event$key }) {
	const event = useFragment(
		graphql`
			fragment CalendarEventsSingleEventCard_Event on Event {
				id
				start
				end
				title
				isAllDay

				...CalendarEvents_meetingInfoModalEvent
			}
		`,
		eventKey,
	);

	const future = !!event.start && new Date(event.start).valueOf() > Date.now();

	const timeString = React.useMemo(() => {
		if (!event.start || !event.end) return null;
		const startDate = new Date(event.start);
		const durationMilliseconds = new Date(event.end).valueOf() - startDate.valueOf();
		return formatEventDateTime(startDate, durationMilliseconds, event.isAllDay);
	}, [event]);

	return (
		<Card>
			<h1>{event.title}</h1>
			<Metadata icon={faCalendar}>
				{future ? "Occurs" : "Occurred"} on: {timeString}
			</Metadata>
			<ViewEventInfoButton event={event} />
		</Card>
	);
}

function formatEventDateTime(startDate: Date, durationMilliseconds: number, isAllDay: boolean): string {
	const endDate = new Date(startDate);
	endDate.setUTCMilliseconds(startDate.getUTCMilliseconds() + durationMilliseconds);

	return meetingDateFormat(startDate, endDate, isAllDay);
}

function EmptyState() {
	return (
		<main className={styles.emptyStateContainer}>
			<EmptyStateIllustration className={styles.emptyStateIllo} />
			<p className={styles.emptyStateText}>
				This series has no calendar event. Learn more about{" "}
				<HelpCenterLink articleId="4892111">notes and calendar events</HelpCenterLink>.
			</p>
		</main>
	);
}

function readMeetingInfoModalEvent(eventKey: CalendarEvents_meetingInfoModalEvent$key) {
	return readInlineData(
		graphql`
			fragment CalendarEvents_meetingInfoModalEvent on Event @inline {
				id
				start
				end
				title
				guid
				location
				recurrenceRule
				isHidden
				isOrganizer
				isAllDay

				conferenceCalls {
					provider
					url
					phoneNumber
					pin
				}
			}
		`,
		eventKey,
	);
}
